import { Fragment, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, post, type AdminStream, type CaptionsView, type StreamsHistory, type TranscodeJobAdmin, type TranscoderInfo } from '../api'
import { canBlend, streamMarkLine, type MarkContext } from '../live-ui/watermark'
import { TRANSCODERS_KEY, TRANSCODE_JOBS_KEY } from './network/Transcoders'
import { Icon } from '../ui'
import { withMono } from '../mono'
import { StreamHistory } from './StreamHistory'
import { PathDelayBlock } from './PathDelay'
import { InputTracks } from '../live-ui/studio/InputTracks'
import { langName } from '../live-ui/captions'
import { Button, NODE_UNKNOWN, Notice, Panel, Tally, duration, healthText, kbps, nodeText } from '../components/ui'
import { useNow } from '../hooks'
import { srtCells, srtLink } from '../live-ui/srt'
import { fillShare } from '../fill'
import { NODES_KEY, fetchNodes } from './Nodes'
import { useRelayLive } from './network/live'
import { RelayDataBanner } from './network/banner'
import { RouteBlock } from './network/RouteBlock'
import type { NoPathStream, RelayRoute } from './network/api'
import type { NodeInfo } from '../api'
import { stampMin } from './network/format'

const unknown = (s: AdminStream) => s.state_reason === 'node_unreachable'

/** Captions of a stream in one line: state, language, lag (docs/captions-overlay.md §10.6). */
function captionsLine(c: CaptionsView) {
  const lang = c.detected_language && c.language === 'auto' ? `auto → ${langName(c.detected_language)}` : langName(c.language)
  return [c.state.replace('_', ' ') + (c.reason ? ` (${c.reason.replace(/_/g, ' ')})` : ''), lang, c.lag_ms !== undefined ? `${(c.lag_ms / 1000).toFixed(1)} s behind` : '']
    .filter(Boolean)
    .join(' · ')
}

export function AdminStreams() {
  const qc = useQueryClient()
  const now = useNow()
  const q = useQuery({ queryKey: ['admin-streams'], queryFn: () => get<AdminStream[]>('/api/admin/streams'), refetchInterval: 2000 })
  // the last 5 minutes of every input (5 s samples): the charts under each live stream
  const hist = useQuery({ queryKey: ['admin-streams-history'], queryFn: () => get<StreamsHistory>('/api/admin/streams/history'), refetchInterval: 5000 })
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState<string | null>(null)
  // WM3 (WMU-08): the free plan's Strevix mark of each stream — its transcoder, or why it is missing
  const marked = !!q.data?.some((s) => s.watermark_effective)
  const tcs = useQuery({ queryKey: TRANSCODERS_KEY, queryFn: () => get<TranscoderInfo[]>('/api/admin/transcoders'), refetchInterval: 3000, enabled: marked })
  const tjobs = useQuery({ queryKey: TRANSCODE_JOBS_KEY, queryFn: () => get<TranscodeJobAdmin[]>('/api/admin/transcode/jobs'), refetchInterval: 3000, enabled: marked })
  const markCtx = (stream: string): MarkContext => {
    const on = (tcs.data ?? []).filter((n) => n.online)
    const job = (tjobs.data ?? []).find((j) => j.stream === stream && j.kind === 'watermark')
    return {
      ...(tcs.data ? { online: on.length, blend: on.filter((n) => canBlend(n.engine?.caps)).length } : {}),
      ...(job ? { node: (tcs.data ?? []).find((n) => n.node_id === job.node_id)?.name || job.node_id, egress: nodes.get(job.egress_node_id)?.name || job.egress_node_id } : {}),
    }
  }
  // Routing network (R0): the route of each stream and the streams without one.
  const relay = useRelayLive()
  const nodesQ = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 10_000, enabled: relay.available === true })
  const nodes = new Map<string, NodeInfo>((nodesQ.data ?? []).map((n) => [n.id, n]))
  const routeOf = new Map<string, RelayRoute>()
  for (const r of relay.routes.values()) {
    const had = routeOf.get(r.stream)
    if (!had || Date.parse(r.created_at) > Date.parse(had.created_at)) routeOf.set(r.stream, r)
  }
  // node relay state: the stream's (fresher) over the polled list
  const relayOf = new Map((nodesQ.data ?? []).map((n) => [n.id, n.relay]))
  for (const n of relay.nodes.values()) relayOf.set(n.id, n.relay)
  const noPathOf = new Map<string, NoPathStream>()
  for (const r of relayOf.values()) for (const np of r?.no_path ?? []) noPathOf.set(np.stream, np)
  const act = async (f: () => Promise<unknown>) => {
    setErr('')
    try {
      await f()
      setConfirm(null)
      await qc.invalidateQueries({ queryKey: ['admin-streams'] })
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  return (
    <Panel title="Streams" aside={<span className="text-sm text-muted">{q.data?.filter((s) => s.live).length ?? 0} live</span>}>
      <RelayDataBanner live={relay} />
      <Notice>{err}</Notice>
      {q.data?.length === 0 && <p className="text-sm text-muted">No stream has reached the media server since it started.</p>}
      <ul className="-mx-4 -mb-4 divide-y divide-line">
        {q.data?.map((s) => (
          <li key={s.stream_name} className="flex flex-wrap items-start gap-3 px-4 py-3" data-testid={`stream-${s.stream_name}`}>
            <span className="pt-1">
              <Tally state={unknown(s) ? 'busy' : s.live ? 'live' : 'dark'} label={unknown(s) ? NODE_UNKNOWN : s.live ? 'Live' : 'Offline'} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {s.email || 'unknown user'} <span className="text-xs text-muted">{s.stream_name}</span>
              </p>
              {s.node && (
                <p className="text-xs text-muted" data-testid={`stream-node-${s.stream_name}`}>
                  Node {nodeText(s.node)}
                </p>
              )}
              {unknown(s) ? (
                <p className="text-xs text-warn" data-testid={`stream-unknown-${s.stream_name}`}>
                  {NODE_UNKNOWN}
                  {s.live ? ' (last seen live)' : ''}
                </p>
              ) : s.live ? (
                <p className="num text-xs text-muted">
                  {duration(s.since, now)} on air, {kbps(s.bitrate_kbps)}, {s.fps.toFixed(0)} fps, keyframe {s.gop_seconds.toFixed(1)} s,{' '}
                  {s.resolution ?? 'resolution unknown'}, {healthText[s.health].toLowerCase()}, {s.outputs} outputs, from {s.publisher}
                  {s.protocol && <span data-testid={`stream-protocol-${s.stream_name}`}> over {s.protocol.toUpperCase()}</span>}
                </p>
              ) : (
                <p className="text-xs text-muted">Offline{s.last_end_reason ? `, last ended: ${s.last_end_reason}` : ''}</p>
              )}
              {s.live && !unknown(s) && <InputBytes s={s} />}
              {s.live && !unknown(s) && s.path_delay && <PathDelayBlock stream={s.stream_name} pd={s.path_delay} paths={hist.data?.paths} />}
              {s.live && !unknown(s) && !!hist.data?.inputs[s.stream_name]?.length && <StreamHistory name={s.stream_name} samples={hist.data.inputs[s.stream_name]!} declaredFps={s.declared_fps} />}
              {s.live && !unknown(s) && s.protocol === 'srt' && s.network?.srt && (
                <p className="num text-xs text-muted" data-testid={`stream-srt-${s.stream_name}`} title="SRT receiver, last 5 s. Resends are normal on a lossy path; problems show in health above.">
                  SRT, last 5 s: {srtCells(srtLink(s.network.srt)).map((c) => `${c.label.toLowerCase()} ${c.value}`).join(', ')}
                </p>
              )}
              {!!s.reasons?.length && s.live && <p className="text-xs text-warn">{s.reasons.join('; ')}</p>}
              {/* E1 (read-only): the stream's Enhanced RTMP tracks, as the user's Input panel lists them */}
              {s.live && !unknown(s) && s.tracks && (
                <div className="mt-2 max-w-[360px]" data-testid={`stream-tracks-${s.stream_name}`}>
                  <InputTracks tracks={s.tracks} warnings={s.track_warnings} now={now} readOnly testId={`stream-track-list-${s.stream_name}`} />
                </div>
              )}
              {relay.available && s.live && <RouteBlock stream={s.stream_name} route={routeOf.get(s.stream_name)} noPath={routeOf.has(s.stream_name) ? undefined : noPathOf.get(s.stream_name)} nodes={nodes} />}
              {s.captions && (
                <p className="num text-xs text-muted" data-testid={`stream-captions-${s.stream_name}`}>
                  Captions: {captionsLine(s.captions)}
                </p>
              )}
              <MarkLine s={s} now={now} ctx={markCtx(s.stream_name)} />
              {s.suspended_until && Date.parse(s.suspended_until) > now && (
                <p className="text-xs text-fail">
                  Suspended {new Date(s.suspended_until).getFullYear() > 9000 ? 'until lifted' : `until ${stampMin(s.suspended_until)}`}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {s.suspended_until && Date.parse(s.suspended_until) > now && (
                <Button variant="quiet" onClick={() => act(() => post(`/api/admin/streams/${s.stream_name}/unsuspend`))}>
                  Lift suspension
                </Button>
              )}
              {s.live &&
                (confirm === s.stream_name ? (
                  <>
                    <Button onClick={() => act(() => del(`/api/admin/streams/${s.stream_name}?mode=soft`))}>Disconnect, allow reconnect</Button>
                    <Button variant="danger" onClick={() => act(() => del(`/api/admin/streams/${s.stream_name}?mode=hard`))}>
                      Stop and suspend
                    </Button>
                    <Button variant="quiet" onClick={() => setConfirm(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="quiet" onClick={() => setConfirm(s.stream_name)}>
                    Stop stream
                  </Button>
                ))}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** Identifiers (node names) whole in mono, the other numbers through withMono. */
function monoIds(text: string, ids: (string | undefined)[]) {
  const list = ids.filter((x): x is string => !!x)
  if (!list.length) return withMono(text)
  const re = new RegExp(`(${list.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`)
  return text.split(re).map((p, i) => (i % 2 ? <span key={i} className="font-mono">{p}</span> : <Fragment key={i}>{withMono(p)}</Fragment>))
}

/** WM3 (WMU-08): "Strevix mark: on · gpu-ewr-1" (muted) or "missing for 42 s — why" (warn, with its icon); nothing for paid plans and with the flag off. */
function MarkLine({ s, now, ctx }: { s: AdminStream; now: number; ctx: MarkContext }) {
  if (!s.live || unknown(s)) return null
  const l = streamMarkLine(s.watermark_effective, now, ctx)
  if (!l) return null
  return (
    <p className={`num text-xs ${l.state === 'missing' ? 'text-warn' : 'text-muted'}`} data-testid={`stream-wm-${s.stream_name}`} data-state={l.state}>
      {l.state === 'missing' && <Icon name="state-warn" size={12} className="mr-1 inline align-[-1px]" />}{monoIds(l.text, [ctx.node, ctx.egress])}
    </p>
  )
}

/**
 * On the wire / useful / filler (docs/bitrate-fill.md §4): bitrate_kbps is the useful video and
 * audio, filler_kbps what the node stripped from the encoder's video (absent = none).
 */
function InputBytes({ s }: { s: AdminStream }) {
  const filler = s.filler_kbps ?? 0
  const wire = s.bitrate_kbps + filler
  const share = fillShare(filler, wire)
  return (
    <p className="num text-xs text-muted" data-testid={`stream-fill-${s.stream_name}`} data-wire={Math.round(wire)} data-useful={Math.round(s.bitrate_kbps)} data-filler={Math.round(filler)}
      title="From the encoder over the last 5 s. Filler data pads the video to a constant bitrate; it is stripped on the way in and written back per output.">
      On the wire {kbps(wire)} · useful {kbps(s.bitrate_kbps)} · filler {kbps(filler)}{share ? ` (${share}%)` : ''}
    </p>
  )
}
